import React, { useEffect, useRef, useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CircularProgress from "@mui/material/CircularProgress";

import Text from "components/common/Text";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import TextField from "components/common/TextField";
import DatePicker from "components/common/DatePicker";

import JarvisButton from "components/JarvisButton";
import { useDispatch, useSelector } from "react-redux";
import { config } from "config/index";
import {
  CKYC_DOC_STATUS,
  getKycDocumentSuccessMessage,
  persistCkycDocStatus,
  readPersistedCkycDocStatus,
} from "helper/kycDocumentUpload";
import {
  areKycDocumentsReadyForSubmit,
  CKYC_FAILED_COPY,
  CKYC_PENDING_COPY,
  CKYC_REUPLOAD_LABEL,
  getCkycCustomerName,
  validateCkycCustomerName,
} from "helper/ckycDocumentVerify";
import { customerApis } from "stores/customer/customerApis";
import { customerActions } from "stores/customer/customerActions";
import { commonActions } from "stores/common/commonActions";

import DoneIcon from "@mui/icons-material/Done";

const isNativeCapacitor =
  config.platform === "ANDROID" || config.platform === "IOS";

const CKYCmanual = (props) => {
  const {
    formData,
    formError,
    isVerified,
    isLoading,
    isImagesUploaded,
    imagesUploadingLoader,
    handleChange,
    onBrowseClick,
    handleReVerification,
    toggleKYCtype,
    disableUpload = {},
    disableFormFields = false,
    documentVerifyStatus = {},
  } = props;

  const dispatch = useDispatch();
  const loginProfile = useSelector((state) => state.customer?.loginProfile);
  const customerName = getCkycCustomerName(loginProfile);
  const customerId = loginProfile?.customerId;
  const persistedStatus = readPersistedCkycDocStatus(customerId);
  const parentHasServerStatus = Object.values(documentVerifyStatus || {}).some(
    (status) =>
      status === CKYC_DOC_STATUS.PENDING ||
      status === CKYC_DOC_STATUS.FAILED ||
      status === CKYC_DOC_STATUS.APPROVED,
  );

  const statusFor = (docType) => {
    const fromParent = documentVerifyStatus?.[docType];
    if (
      fromParent === CKYC_DOC_STATUS.PENDING ||
      fromParent === CKYC_DOC_STATUS.FAILED ||
      fromParent === CKYC_DOC_STATUS.APPROVED
    ) {
      return fromParent;
    }
    if (parentHasServerStatus) {
      return CKYC_DOC_STATUS.IDLE;
    }
    const persisted = persistedStatus?.[docType];
    if (
      persisted === CKYC_DOC_STATUS.PENDING ||
      persisted === CKYC_DOC_STATUS.FAILED
    ) {
      return persisted;
    }
    if (formError?.[docType]) {
      return CKYC_DOC_STATUS.FAILED;
    }
    return CKYC_DOC_STATUS.IDLE;
  };

  const resolvedStatus = {
    panCard: statusFor("panCard"),
    aadharCardFront: statusFor("aadharCardFront"),
    aadharCardBack: statusFor("aadharCardBack"),
  };

  const canSubmit = areKycDocumentsReadyForSubmit(
    isImagesUploaded,
    disableUpload,
    resolvedStatus,
  );

  const hasPending = Object.values(resolvedStatus).includes(
    CKYC_DOC_STATUS.PENDING,
  );
  const hasFailed = Object.values(resolvedStatus).includes(
    CKYC_DOC_STATUS.FAILED,
  );
  const canEditName = hasFailed && !hasPending && !isVerified && !disableFormFields;

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(customerName);
  const [nameError, setNameError] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    setNameDraft(customerName);
  }, [customerName]);

  useEffect(() => {
    persistCkycDocStatus(customerId, resolvedStatus);
  }, [customerId, resolvedStatus.panCard, resolvedStatus.aadharCardFront, resolvedStatus.aadharCardBack]);

  const getFieldError = (name) => {
    const message = formError?.[name];
    if (!message) {
      return "";
    }
    const value = formData?.[name];
    if (
      message === "This field is required" &&
      String(value ?? "").trim() !== ""
    ) {
      return "";
    }
    return message;
  };

  const panCardRef = useRef();
  const aadharCardFrontRef = useRef();
  const aadharCardBackRef = useRef();

  const [browseMenu, setBrowseMenu] = useState(null);

  const handleCloseBrowseMenu = () => setBrowseMenu(null);

  const handleMenuFromGallery = () => {
    const docType = browseMenu?.docType;
    handleCloseBrowseMenu();
    if (docType) OnBrowseFileClick(docType);
  };

  const handleMenuTakePhoto = () => {
    const docType = browseMenu?.docType;
    handleCloseBrowseMenu();
    if (docType) onBrowseClick({ type: docType, source: "camera" });
  };

  const getPanFormattedValue = (value = "") => {
    const normalizedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    let formattedValue = "";

    for (const ch of normalizedValue) {
      if (formattedValue.length < 5) {
        if (/[A-Z]/.test(ch)) {
          formattedValue += ch;
        }
      } else if (formattedValue.length < 9) {
        if (/[0-9]/.test(ch)) {
          formattedValue += ch;
        }
      } else if (formattedValue.length === 9) {
        if (/[A-Z]/.test(ch)) {
          formattedValue += ch;
        }
      } else {
        break;
      }
    }

    return formattedValue;
  };

  const handleOnChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    onBrowseClick({ type, data: file });
  };
  function OnBrowseFileClick(type) {
    if (type === "panCard") {
      panCardRef.current?.click();
    } else if (type === "aadharCardFront") {
      aadharCardFrontRef.current?.click();
    } else if (type === "aadharCardBack") {
      aadharCardBackRef.current?.click();
    }
  }

  const handleSaveName = async () => {
    const error = validateCkycCustomerName(nameDraft);
    if (error) {
      setNameError(error);
      return;
    }
    setSavingName(true);
    try {
      const fullName = String(nameDraft).trim();
      await customerApis.updateProfileDetails({ fullName });
      dispatch(customerActions.updateFullName(fullName));
      dispatch(commonActions.setToast({ message: "Name updated successfully" }));
      setEditingName(false);
      setNameError("");
    } catch (err) {
      setNameError(err?.message || "Could not update name");
    } finally {
      setSavingName(false);
    }
  };

  const isFormLocked = isVerified || disableFormFields;
  const panStatus = resolvedStatus.panCard;
  const aadhaarFrontStatus = resolvedStatus.aadharCardFront;
  const aadhaarBackStatus = resolvedStatus.aadharCardBack;
  const panFieldsLocked =
    isFormLocked ||
    panStatus === CKYC_DOC_STATUS.PENDING ||
    panStatus === CKYC_DOC_STATUS.APPROVED;
  const aadhaarNumberLocked =
    isFormLocked ||
    aadhaarFrontStatus === CKYC_DOC_STATUS.PENDING ||
    aadhaarFrontStatus === CKYC_DOC_STATUS.APPROVED ||
    aadhaarBackStatus === CKYC_DOC_STATUS.PENDING ||
    aadhaarBackStatus === CKYC_DOC_STATUS.APPROVED;

  const renderUploadActions = (docType) => {
    const status = statusFor(docType);
    const loading = imagesUploadingLoader[docType];

    const actionsRowSx = {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-end",
      flexShrink: 0,
      flexWrap: "nowrap",
      gap: 0.75,
    };

    if (status === CKYC_DOC_STATUS.PENDING) {
      return (
        <Stack direction="row" sx={actionsRowSx}>
          <CircularProgress size={16} />
          <Text variant="small" sx={{ color: "#808591", whiteSpace: "nowrap" }}>
            Under review
          </Text>
        </Stack>
      );
    }

    if (
      status === CKYC_DOC_STATUS.APPROVED ||
      status === CKYC_DOC_STATUS.UPLOADED
    ) {
      return (
        <DoneIcon
          sx={{ color: "#00db00", fontSize: "20px", flexShrink: 0 }}
        />
      );
    }

    const buttonLabel =
      status === CKYC_DOC_STATUS.FAILED
        ? CKYC_REUPLOAD_LABEL[docType]
        : "Browse";

    if (isNativeCapacitor) {
      return (
        <Stack direction="row" sx={actionsRowSx}>
          <JarvisButton
            variant="outlined"
            disableEndIcon={false}
            endIcon={
              <KeyboardArrowDownIcon sx={{ fontSize: "18px !important" }} />
            }
            sx={{
              fontSize: "14px",
              padding: "6px 16px",
              borderRadius: "50px !important",
              flexShrink: 0,
            }}
            onClick={(e) =>
              setBrowseMenu({ anchorEl: e.currentTarget, docType })
            }
            loading={loading}
          >
            {buttonLabel}
          </JarvisButton>
        </Stack>
      );
    }

    return (
      <Stack direction="row" sx={actionsRowSx}>
        <JarvisButton
          disableEndIcon
          variant="outlined"
          sx={{
            fontSize: "14px",
            padding: "6px 20px",
            borderRadius: "50px !important",
            flexShrink: 0,
          }}
          onClick={() => OnBrowseFileClick(docType)}
          loading={loading}
        >
          {buttonLabel}
        </JarvisButton>
      </Stack>
    );
  };

  const renderDocumentUploadRow = (label, docType, { marginTop } = {}) => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="nowrap"
      gap={1}
      sx={{
        maxWidth: 400,
        width: "100%",
        minHeight: 56,
        boxSizing: "border-box",
        border: "1px dashed #E0E7FF",
        backgroundColor: "#FCFDFF",
        px: 1.75,
        py: 1.25,
        mb: 0.5,
        ...(marginTop ? { mt: marginTop } : {}),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          flex: 1,
          minWidth: 0,
          gap: 1,
          pr: 1,
        }}
      >
        <Box
          component="img"
          src="/images/img-icon.svg"
          alt=""
          sx={{ width: 20, height: 20, flexShrink: 0, display: "block" }}
        />
        <Text
          component="span"
          title={label}
          sx={{
            color: "#808591",
            fontSize: "14px",
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Text>
      </Stack>
      {renderUploadActions(docType)}
    </Stack>
  );

  const renderDocStatus = (docType) => {
    const status = statusFor(docType);
    const error = formError[docType];

    if (status === CKYC_DOC_STATUS.PENDING) {
      return (
        <Stack sx={{ pl: 1.5, mb: 0.5 }}>
          <Text variant="small" sx={{ color: "#B45309", fontWeight: 600 }}>
            {CKYC_PENDING_COPY.title}
          </Text>
          <Text variant="small" sx={{ color: "#808591" }}>
            {CKYC_PENDING_COPY.body}
          </Text>
        </Stack>
      );
    }

    if (status === CKYC_DOC_STATUS.FAILED || error) {
      return (
        <Stack sx={{ pl: 1.5, mb: 0.5 }}>
          <Text variant="small" sx={{ color: "#d32f2f", fontWeight: 600 }}>
            {CKYC_FAILED_COPY.title}
          </Text>
          <Text variant="small" sx={{ color: "#d32f2f" }}>
            {error || CKYC_FAILED_COPY.body}
          </Text>
        </Stack>
      );
    }

    if (status === CKYC_DOC_STATUS.APPROVED) {
      return (
        <Text variant="small" sx={{ color: "#1B8A3E", pl: 1.5 }}>
          {getKycDocumentSuccessMessage()}
        </Text>
      );
    }

    return null;
  };

  return (
    <Box>
      <Text variant="subtitle2" sx={{ fontWeight: "bold", mb: 2.5 }}>
        Complete CKYC to start investing
      </Text>

      <Text variant="body2" sx={{ color: "#3A3A3A", mb: 3, maxWidth: "400px" }}>
        We need your Identity card photos to get <br /> you verified manually.
        {/* <Text
          component="span"
          sx={{
            textDecoration: "underline",
            cursor: "pointer",
            color: "blue",
            "&:hover": {
              color: "#e60b0b",
            },
          }}
          onClick={() => toggleKYCtype(false)}
        >
          {` `}Click here for Auto CKYC
        </Text> */}
      </Text>

      {hasPending ? (
        <Text variant="small" sx={{ color: "#B45309", mb: 2, maxWidth: 400 }}>
          {CKYC_PENDING_COPY.title}. {CKYC_PENDING_COPY.body}
        </Text>
      ) : null}

      {hasFailed && !hasPending ? (
        <Text variant="small" sx={{ color: "#d32f2f", mb: 2, maxWidth: 400 }}>
          {CKYC_FAILED_COPY.body}
        </Text>
      ) : null}

      {canEditName && editingName ? (
        <Stack sx={{ maxWidth: 400, mb: 3 }} gap={1}>
          <TextField
            type="text"
            label="Name on record"
            value={nameDraft}
            onChange={(e) => {
              setNameDraft(e.target.value);
              setNameError("");
            }}
            helperText={nameError}
            error={Boolean(nameError)}
            sx={{ color: "#808591" }}
          />
          <Stack direction="row" gap={1}>
            <JarvisButton
              disableEndIcon
              loading={savingName}
              onClick={handleSaveName}
              sx={{ fontSize: "14px", padding: "6px 16px" }}
            >
              Save name
            </JarvisButton>
            <JarvisButton
              disableEndIcon
              variant="outlined"
              onClick={() => {
                setEditingName(false);
                setNameDraft(customerName);
                setNameError("");
              }}
              sx={{ fontSize: "14px", padding: "6px 16px" }}
            >
              Cancel
            </JarvisButton>
          </Stack>
        </Stack>
      ) : (
        <Stack sx={{ maxWidth: 400, mb: 3 }}>
          <TextField
            type="text"
            label="Name on record"
            value={customerName}
            helperText={formError.customerName}
            error={Boolean(formError.customerName)}
            disabled
            sx={{ color: "#808591" }}
          />
          {canEditName ? (
            <Text
              component="span"
              sx={{
                mt: 0.75,
                color: "#1976d2",
                cursor: "pointer",
                fontSize: "14px",
                textDecoration: "underline",
                width: "fit-content",
              }}
              onClick={() => {
                setNameDraft(customerName);
                setEditingName(true);
              }}
            >
              Edit Name
            </Text>
          ) : null}
        </Stack>
      )}

      {/* Upload PAN */}
      <Stack mb={3}>
        <input
          type="file"
          ref={panCardRef}
          hidden
          multiple={false}
          onChange={(e) => handleOnChange(e, "panCard")}
          onClick={(event) => {
            event.target.value = null;
          }}
          accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf,.pdf"
        />
        <input
          type="file"
          ref={aadharCardFrontRef}
          hidden
          multiple={false}
          onChange={(e) => handleOnChange(e, "aadharCardFront")}
          onClick={(event) => {
            event.target.value = null;
          }}
          accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf,.pdf"
        />
        <input
          type="file"
          ref={aadharCardBackRef}
          hidden
          multiple={false}
          onChange={(e) => handleOnChange(e, "aadharCardBack")}
          onClick={(event) => {
            event.target.value = null;
          }}
          accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf,.pdf"
        />
        <Text>PAN Details</Text>
        <TextField
          type="text"
          label="PAN No."
          value={formData.panNumber}
          onChange={(e) => {
            const typedChar = e.nativeEvent?.data || "";
            const formattedPan = getPanFormattedValue(e.target.value);

            // Some Android IMEs can emit only the latest typed character and
            // replace the whole value in controlled inputs. Preserve previous PAN.
            const nextPanValue =
              !formattedPan && typedChar && formData.panNumber
                ? getPanFormattedValue(`${formData.panNumber}${typedChar}`)
                : formattedPan;

            handleChange({
              name: "panNumber",
              value: nextPanValue,
            });
          }}
          helperText={getFieldError("panNumber")}
          error={Boolean(getFieldError("panNumber"))}
          disabled={panFieldsLocked}
          sx={{ color: "#808591", maxWidth: 400, mb: "16px" }}
          inputProps={{
            maxLength: 10,
            autoCapitalize: "characters",
            autoCorrect: "off",
            spellCheck: false,
            inputMode: "text",
          }}
        />
        <DatePicker
          label="Date of Birth"
          value={formData.dateOfBirth}
          onChange={(value) => handleChange({ name: "dateOfBirth", value })}
          helperText={getFieldError("dateOfBirth")}
          error={Boolean(getFieldError("dateOfBirth"))}
          disabled={panFieldsLocked}
          textFieldProps={{
            sx: { maxWidth: 400, mb: 2 },
          }}
        />
        {renderDocumentUploadRow("Upload PAN Photo", "panCard")}
        {renderDocStatus("panCard")}
      </Stack>

      {/* Upload Aadhar */}
      <Stack mb={3}>
        <Text>Aadhaar Details</Text>
        <TextField
          type="number"
          label="Aadhaar No."
          value={formData.aadharNumber}
          onChange={(e) =>
            handleChange({
              name: "aadharNumber",
              value: e.target.value.toUpperCase(),
            })
          }
          helperText={getFieldError("aadharNumber")}
          error={Boolean(getFieldError("aadharNumber"))}
          disabled={aadhaarNumberLocked}
          sx={{ color: "#808591", maxWidth: "400px", mb: "16px" }}
          inputProps={{ maxLength: 12 }}
        />
        {renderDocumentUploadRow("Upload Aadhaar Front", "aadharCardFront")}
        {renderDocStatus("aadharCardFront")}

        {renderDocumentUploadRow("Upload Aadhaar Back", "aadharCardBack", {
          marginTop: 1.5,
        })}
        {renderDocStatus("aadharCardBack")}
      </Stack>

      {/* Submit re-verification */}
      {!isFormLocked && (
        <>
          <JarvisButton
            loading={isLoading}
            disabled={!canSubmit}
            onClick={handleReVerification}
            sx={{ mb: { xs: 1, md: 1 }, width: { xs: "100%", md: "auto" } }}
          >
            Submit for re-verification
          </JarvisButton>
          {!canSubmit ? (
            <Text variant="small" sx={{ color: "#808591", mb: { xs: 8, md: 3 } }}>
              {hasPending
                ? "Our team is reviewing your documents. You can submit after they approve."
                : hasFailed
                  ? "Re-upload rejected documents to continue."
                  : "Upload PAN and Aadhaar. Submit after our team approves all documents."}
            </Text>
          ) : (
            <Box sx={{ mb: { xs: 8, md: 3 } }} />
          )}
        </>
      )}

      {isNativeCapacitor ? (
        <Menu
          anchorEl={browseMenu?.anchorEl}
          open={Boolean(browseMenu?.anchorEl)}
          onClose={handleCloseBrowseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: { minWidth: 168, mt: 0.5 },
          }}
        >
          <MenuItem onClick={handleMenuFromGallery} dense>
            From Gallery
          </MenuItem>
          <MenuItem onClick={handleMenuTakePhoto} dense>
            Take Photo
          </MenuItem>
        </Menu>
      ) : null}
    </Box>
  );
};

export default CKYCmanual;
